/**
 * Settings → Channels return-window editor form helpers (M25 #436/#437).
 *
 * The server (`GET …/return-windows/`) returns one resolved row per SaleChannel:
 * `{channel, days, is_default}` — `days` is the org override or the per-channel
 * default, `is_default` true when no override row exists. The editor renders a
 * days input per row; `PUT` takes only `[{channel, days}]` and upserts them.
 * These helpers keep the controlled inputs + the changed-only diff pure/tested.
 */


/** Map the server rows onto the editor's controlled string state ({channel: "30"}). */
function returnWindowDefaults(rows) {
  const out = {};
  for (const row of rows || []) {
    out[row.channel] = String(row.days);
  }
  return out;
}


/** True when `value` is a whole number of days inside the backend's 1..365 range
 * (mirrors `ReturnWindowSettingSerializer.days` — smallint-safe, product sanity). */
function isValidDays(value) {
  if (value === '' || value === null || value === undefined) {
    return false;
  }
  const days = Number(value);
  return Number.isInteger(days) && days >= 1 && days <= 365;
}


/**
 * Build the PUT body: only the rows the user actually changed, and only valid
 * 1..365 integers. Compares the edited string state against the loaded server
 * rows so an untouched (or invalid / blank) field is never sent — the PUT is a
 * last-wins partial upsert, so sending unchanged rows is wasteful and sending
 * an out-of-range value would 400 the whole request.
 */
function buildReturnWindowPayload(edited, rows) {
  const initial = {};
  for (const row of rows || []) {
    initial[row.channel] = row.days;
  }
  const payload = [];
  for (const channel of Object.keys(edited || {})) {
    const raw = edited[channel];
    if (!isValidDays(raw)) {
      continue;
    }
    const days = Number(raw);
    if (days === initial[channel]) {
      continue;
    }
    payload.push({ channel, days });
  }
  return payload;
}


export { buildReturnWindowPayload, isValidDays, returnWindowDefaults };
